import { LNGS_NAMESPACE_LOGICAL_DEVICE } from '@ingenico/util-i18n-config';
import { isEmpty, toString } from 'lodash-es';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SoftwareType } from '../../../../domain/models/selectItems.model';
import { FileOutputFormularyModalSchemaKeys } from '../../useFileOutputFormularyModal.hook';
import { SelectorField } from '../SelectorField/SelectorField.ctrl';
import type { FieldsProps } from '../fieldsModel.model';

export function VersionField({ form, options, isLoading, onFilterChange, moduleFilterChange }: FieldsProps): ReactNode {
  const { t } = useTranslation(Object.values(LNGS_NAMESPACE_LOGICAL_DEVICE));

  const { watch, control, resetField } = form;

  const [softwareType, softwareNameId, versionIdField] = watch([
    FileOutputFormularyModalSchemaKeys.SoftwareTypeField,
    FileOutputFormularyModalSchemaKeys.SoftwareNameIdField,
    FileOutputFormularyModalSchemaKeys.VersionIdField,
  ]);

  const isAnyConcatenatedFieldsEmpty = useMemo(() => {
    return softwareType === SoftwareType.None || isEmpty(softwareNameId);
  }, [softwareNameId, softwareType]);

  const optionsIds = useMemo(() => {
    return options?.map(({ id }) => id) ?? [];
  }, [options]);

  const findOptionName = useCallback(
    (targetId: string) => {
      return options?.find(({ id }) => targetId === id)?.label ?? '';
    },
    [options],
  );

  const handleInputChange = useCallback(
    (evt: unknown, value: string, reason?: string) => {
      if (reason === 'clear' || reason === 'input') {
        onFilterChange(value);
      }
    },
    [onFilterChange],
  );

  const inputTextValue = useMemo(
    () => toString(options?.find((option) => option.id === versionIdField)),
    [options, versionIdField],
  );

  const clearNextFields = useCallback(() => {
    resetField('moduleId');
    if (moduleFilterChange) {
      moduleFilterChange('');
    }
  }, [moduleFilterChange, resetField]);

  return (
    <SelectorField
      control={control}
      onChangeTrigger={clearNextFields}
      dataErrorTestId={'version-error-field-data-id'}
      dataTestId={'version-field-data-id'}
      fieldName={'versionId'}
      getOptionLabel={(field) => findOptionName(field)}
      inputLabel={t('fileOutputModal.fields.version')}
      isDisabled={isAnyConcatenatedFieldsEmpty}
      isLoading={isLoading}
      isReadOnly={isAnyConcatenatedFieldsEmpty}
      options={optionsIds}
      onInputChange={handleInputChange}
      inputValue={inputTextValue}
    />
  );
}
